import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { HOST_API } from 'src/config';
import ConsentBrandHeader from 'src/pages/public/components/ConsentBrandHeader';

// The storage bucket does not allow every Patient Hub origin. Reuse the
// existing public image proxy only for the server-provided practice logo.
export function practiceLogoCaptureUrl(logoUrl, apiBase = HOST_API) {
    if (typeof logoUrl !== 'string' || !/^(https?:\/\/|\/(?!\/))/.test(logoUrl)) return '';
    try {
        const source = new URL(logoUrl, apiBase);
        if (!['http:', 'https:'].includes(source.protocol) || source.username || source.password) return '';
        if (!apiBase) return source.href;
        return `${apiBase.replace(/\/$/, '')}/api/v1/utils/website-proxy/?url=${encodeURIComponent(source.href)}`;
    } catch { return ''; }
}

// Serialise captures of a shared DOM root so one request cannot remove another
// request's header/details while html2canvas is still reading them.
const captureQueues = new WeakMap();
export async function acquireConsentPdfCapture(element) {
    const previous = captureQueues.get(element) || Promise.resolve();
    let release;
    const current = new Promise((resolve) => { release = resolve; });
    captureQueues.set(element, current);
    await previous;
    return () => {
        if (captureQueues.get(element) === current) captureQueues.delete(element);
        release();
    };
}

// Capture-only: insert once in normal document flow, then restore the exact
// authored DOM. Callers supply the form's server-scoped CRM branding, never a
// cached globally selected practice. Clinical fields and values are untouched.
export function withConsentPdfBranding(element, branding) {
    if (!branding || typeof branding !== 'object') return () => {};
    const practiceName = typeof branding.practice_name === 'string' ? branding.practice_name.trim() : '';
    const addressLines = Array.isArray(branding.address_lines)
        ? branding.address_lines.filter((line) => typeof line === 'string' && line.trim()).map((line) => line.trim()) : [];
    const logoUrl = practiceLogoCaptureUrl(branding.logo_url);
    if (!practiceName && !addressLines.length && !logoUrl) return () => {};
    const primaryColor = /^#[0-9a-f]{6}$/i.test(branding.brand_colour || '') ? branding.brand_colour : undefined;
    const existing = Array.from(element.querySelectorAll('[data-consent-brand-header]'))
        .map((node) => ({ node, style: node.getAttribute('style') }));
    const container = document.createElement('div');
    container.setAttribute('data-pdf-practice-branding', '');
    element.prepend(container);
    let root;
    const cleanup = () => {
        try {
            if (root) flushSync(() => root.unmount());
        } finally {
            container.remove();
            existing.forEach(({ node, style }) => {
                if (style === null) node.removeAttribute('style');
                else node.setAttribute('style', style);
            });
        }
    };
    try {
        root = createRoot(container);
        existing.forEach(({ node }) => { node.style.display = 'none'; });
        flushSync(() => root.render(<ConsentBrandHeader pdfMode logoUrl={logoUrl} practiceName={practiceName} addressLines={addressLines} primaryColor={primaryColor} />));
    } catch (error) {
        cleanup();
        throw error;
    }
    return cleanup;
}
